// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// A dedicated request function for handling FormData
const requestFormData = async (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;

  // For FormData requests, leave headers empty so that the browser sets multipart boundaries.
  // Also, do not perform any serialization on the body.
  const fetchOptions = {
    credentials: options.credentials || 'include',
    headers: options.headers || {}, // leave headers empty
    ...options,
  };

  const response = await fetch(url, fetchOptions);
  return await response.json();
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

/**
 * Makes a POST request to the local API endpoint with a FormData body.
 *
 * @param {string} path - the path of the API endpoint
 * @param {FormData} formData - the form data to send with the request
 * @param {Object} [options] - additional options to pass to the request
 * @returns {Promise} resolves to the response of the request
 */
const postFormData = async (path, formData, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body: formData,
  };
  return await requestFormData(path, requestOptions);
};
// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

/**
 * API endpoints for interacting with AWS.
 *
 * @namespace AWSApi
 */

export const AWSApi = {
  /**
   * Uploads a file to S3.
   *
   * @function uploadFile
   * @memberof AWSApi
   * @param {Object} body - the form data to send in the request body
   * @returns {Promise} resolves to the response of the request
   */
  uploadFile: body => postFormData('/api/upload-file', body),
  /**
   * Deletes a file from S3.
   *
   * @function deleteFile
   * @memberof AWSApi
   * @param {Object} body - the form data to send in the request body
   * @returns {Promise} resolves to the response of the request
   */
  deleteFile: body => post('/api/delete-file', body),
};

// Shippo API
// This section handles API calls related to Shippo for shipping addresses, carriers, rates, shipments, and labels.
// It mainly uses the `post` method to communicate with the server.

export const ShippoApi = {
  /**
   * Create a shipping address using Shippo.
   * @param {Object} body - The data to send for creating the address.
   * @returns {Promise} - A promise that resolves to the result of the address creation.
   */
  createAddress: body => post('/api/shippo/create-address', body),

  /**
   * Get available carriers from Shippo.
   * @param {Object} body - The data to send for fetching carriers.
   * @returns {Promise} - A promise that resolves to the list of carriers.
   */
  getCarriers: body => post('/api/shippo/get-carriers', body),

  /**
   * Create a shipping label using Shippo.
   * @param {Object} body - The data to send for creating the label.
   * @returns {Promise} - A promise that resolves to the result of the label creation.
   */
  createLabel: body => post('/api/shippo/create-label', body),

  /**
   * Create a return shipping label using Shippo.
   * @param {Object} body - The data to send for creating the return label.
   * @returns {Promise} - A promise that resolves to the result of the return label creation.
   */
  createReturnLabel: body => post('/api/shippo/create-return-label', body),

  /**
   * Get tracking status for a shipment using Shippo.
   * @param {Object} body - The data containing the transaction ID.
   * @returns {Promise} - A promise that resolves to the tracking status of the shipment.
   */
  getTrackingStatus: body =>
    get(`/api/shippo/get-tracking?transactionId=${body.transactionId}`),
};


/**
 * API endpoints for interacting with Cloudinary.
 *
 * @namespace CloudinaryApi
 */
export const CloudinaryApi = {
  /**
   * Uploads a file to Cloudinary.
   *
   * @function uploadFile
   * @memberof CloudinaryApi
   * @param {FormData} formData - FormData containing { file: File, userId: string }
   * @returns {Promise} resolves to { data: { publicId, secureUrl, resourceType, ... }, message }
   */
  uploadFile: formData => postFormData('/api/cloudinary/upload-file', formData),

  /**
   * Deletes a file from Cloudinary.
   *
   * @function deleteFile
   * @memberof CloudinaryApi
   * @param {Object} body - JSON body containing { key: publicId, resourceType?: 'image' | 'video' }
   * @returns {Promise} resolves to the server response
   */
  deleteFile: body => post('/api/cloudinary/delete-file', body),
};

export const cloudinaryAPI = {
  /**
   * Uploads a file to S3.
   *
   * @function uploadFile
   * @memberof cloudinaryAPI
   * @param {Object} body - the form data to send in the request body
   * @returns {Promise} resolves to the response of the request
   */
  uploadFile: body => postFormData('/api/cloudinary/upload-file', body),
  /**
   * Deletes a file from S3.
   *
   * @function deleteFile
   * @memberof cloudinaryAPI
   * @param {Object} body - the form data to send in the request body
   * @returns {Promise} resolves to the response of the request
   */
  deleteFile: body => post('/api/cloudinary/delete-file', body),
};
